// apps/provider/src/services/api/client.js
// Base API client with HTTP infrastructure: request(), publicRequest(), _queuedFetch()
// Token management, error handling, FormData support, request queue integration

import { authService, isNetworkError } from '../auth';
import requestQueue, { PRIORITY } from '../requestQueue';

const XANO_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.rehabilitationhealth.com/api:RIB0KqtX';
const XANO_TESTING_BASE_URL = 'https://test-api.rehabilitationhealth.com/api:RIB0KqtX';

// Veradigm token management
const VERADIGM_TOKEN_KEY = 'veradigm_token';
const VERADIGM_TIMESTAMP_KEY = 'veradigm_token_timestamp';
const VERADIGM_TOKEN_TIMEOUT_MS = 19 * 60 * 1000; // 19 minutes (1 min buffer)

// Request timeout (2026-09-22). Without one, a request stuck behind a stalled
// backend held one of the tab's 4 queue slots forever; during the 9/22 outage
// that froze tabs and drove reloads. Callers that legitimately run long
// (exports, uploads, batch creates, integrations) pass `timeoutMs` per call;
// 0 disables. A timeout rejects with name "TimeoutError", distinct from a
// caller cancel ("AbortError"), so stores can surface it instead of going quiet.
const DEFAULT_TIMEOUT_MS = 30000;

export function timeoutError() {
  return new DOMException('Request timed out', 'TimeoutError');
}

// A controller that follows the caller's signal and also fires after timeoutMs.
export function withTimeout(callerSignal, timeoutMs) {
  const controller = new AbortController();
  let timer = null;
  let timedOut = false;
  const onCallerAbort = () => controller.abort(callerSignal?.reason);
  if (callerSignal) {
    if (callerSignal.aborted) controller.abort(callerSignal.reason);
    else callerSignal.addEventListener('abort', onCallerAbort, { once: true });
  }
  if (timeoutMs > 0) {
    timer = setTimeout(() => {
      timedOut = true;
      controller.abort(timeoutError());
    }, timeoutMs);
  }
  return {
    signal: controller.signal,
    didTimeOut: () => timedOut,
    clear: () => {
      if (timer) clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}

export class ApiClient {
  constructor() {
    this.baseUrl = XANO_BASE_URL;
  }

  /**
   * Internal fetch wrapper that goes through the request queue. Resolves with
   * the parsed JSON body (the body is read inside the queue slot and the
   * timeout window).
   * @param {string} url - Full URL to fetch
   * @param {Object} fetchOptions - Standard fetch options
   * @param {Object} queueOptions - Queue options (priority, label)
   */
  async _queuedFetch(url, fetchOptions = {}, queueOptions = {}) {
    const {
      priority = PRIORITY.NORMAL,
      label = 'api-request',
      withAuth = false,
      timeoutMs = DEFAULT_TIMEOUT_MS,
    } = queueOptions;

    return requestQueue.enqueue(
      async () => {
        // The timer starts at execution, not enqueue: time spent waiting for a
        // slot is not the server's fault. The body is read inside the same
        // window so the timeout and the caller's abort cover the download too
        // (a 1.6 MB calendar body is real work, and a stalled body used to
        // hang the caller after the slot was freed).
        const timeout = withTimeout(fetchOptions.signal, timeoutMs);
        const options = { ...fetchOptions, signal: timeout.signal };
        if (withAuth) {
          // Read the token at execution time, not enqueue time, so requests
          // queued behind a refresh pick up the new token
          options.headers = {
            ...options.headers,
            'Authorization': `Bearer ${authService.getToken()}`
          };
        }
        try {
          const response = await fetch(url, options);

          // Attach status to error for retry logic
          if (!response.ok) {
            const error = new Error(`HTTP error! status: ${response.status}`);
            error.status = response.status;
            // Best-effort: surface the backend's error message (e.g. Xano
            // precondition text) so callers can show it to the user
            try {
              const body = await response.json();
              if (body?.message) error.apiMessage = body.message;
            } catch { /* non-JSON body */ }
            throw error;
          }

          return await response.json();
        } catch (error) {
          if (timeout.didTimeOut()) throw timeoutError();
          throw error;
        } finally {
          timeout.clear();
        }
      },
      {
        priority,
        label,
        signal: fetchOptions.signal,
        // Only reads are replayed by the queue: a mutation whose response was
        // lost may already have run on the backend (2026-10-14)
        retryable: (fetchOptions.method || 'GET').toUpperCase() === 'GET'
      }
    );
  }

  async publicRequest(endpoint, options = {}) {
    const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;
    const timeout = withTimeout(fetchOptions.signal, timeoutMs);
    try {
      const headers = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers
      };

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: timeout.signal
      });

      if (!response.ok) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        // Best-effort: surface the backend's error message (e.g. the public
        // scheduling form's "That time was just taken") as on request()
        try {
          const body = await response.json();
          if (body?.message) error.apiMessage = body.message;
        } catch { /* non-JSON body */ }
        throw error;
      }

      return await response.json();
    } catch (error) {
      const err = timeout.didTimeOut() ? timeoutError() : error;
      console.error('[API] Public request failed:', err);
      throw err;
    } finally {
      timeout.clear();
    }
  }

  // Create our own request method with the correct base URL
  async request(endpoint, options = {}, _methodName = null, queueOptions = {}) {
    options._methodName = _methodName || 'unknown';
    if (!authService.getToken()) {
      throw new Error('No authentication token found');
    }

    // `timeoutMs` is a request option, not a fetch option
    const { timeoutMs, ...requestOptions } = options;

    const headers = {};
    // Only add Content-Type for non-FormData requests
    if (!(requestOptions.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Authorization is injected at execution time by _queuedFetch (withAuth)
    const fetchOptions = {
      ...requestOptions,
      headers: {
        ...headers,
        ...requestOptions.headers
      }
    };
    const resolvedQueueOptions = {
      priority: queueOptions.priority || PRIORITY.NORMAL,
      label: queueOptions.label || _methodName || `request:${endpoint}`,
      withAuth: true,
      timeoutMs: timeoutMs ?? queueOptions.timeoutMs ?? DEFAULT_TIMEOUT_MS
    };

    try {
      return await this._queuedFetch(`${this.baseUrl}${endpoint}`, fetchOptions, resolvedQueueOptions);
    } catch (error) {
      // Caller-initiated aborts are not failures and must never trigger the
      // 401 refresh-and-retry path (the retry would reuse the dead signal);
      // our own timeouts are terminal for the same reason
      if (error.name === 'AbortError' || error.name === 'TimeoutError') {
        throw error;
      }

      // 403 is a permission denial, not an auth failure — never clear the
      // token for it. 401 usually means the token expired: refresh and retry
      // the request exactly once.
      if (error.status !== 401) {
        console.error('[API] Request failed:', error);
        throw error;
      }

      if (fetchOptions.signal?.aborted) {
        throw error;
      }

      try {
        await authService.refreshXanoToken();
      } catch (refreshError) {
        // Non-network refresh failure means we cannot mint a new token;
        // terminal Auth0 failures also fire auth:session-expired for the UI
        if (!isNetworkError(refreshError)) {
          authService.clearToken();
        }
        console.error('[API] Token refresh failed:', refreshError);
        throw error;
      }

      // A repeat 401 after a successful refresh is an endpoint-level
      // unauthorized precondition, not expiry — do not clear the fresh token
      return await this._queuedFetch(`${this.baseUrl}${endpoint}`, fetchOptions, {
        ...resolvedQueueOptions,
        label: `${resolvedQueueOptions.label}:retry`
      });
    }
  }

}

export { PRIORITY, XANO_BASE_URL, XANO_TESTING_BASE_URL, VERADIGM_TOKEN_KEY, VERADIGM_TIMESTAMP_KEY, VERADIGM_TOKEN_TIMEOUT_MS, DEFAULT_TIMEOUT_MS };
